
import { useState } from "react";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle,
  CardFooter
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { 
  KitSolar, 
  ParametrosFinanceiros, 
  DadosInstalacao,
  DadosIrradiacao
} from "@/types";
import { 
  Calculator, 
  Calendar, 
  TrendingUp, 
  ChartBar,
  CircleDollarSign,
  ClipboardCheck,
  ChartPie
} from "lucide-react";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { 
  CartesianGrid, 
  Line, 
  LineChart, 
  XAxis, 
  YAxis, 
  Tooltip, 
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie
} from "recharts";

interface ResultadoFinanceiroDisplayProps {
  kitSolar: KitSolar;
  parametrosFinanceiros: ParametrosFinanceiros;
  dadosInstalacao: DadosInstalacao;
  dadosIrradiacao: DadosIrradiacao;
}

export const ResultadoFinanceiroDisplay = ({
  kitSolar,
  parametrosFinanceiros,
  dadosInstalacao,
  dadosIrradiacao
}: ResultadoFinanceiroDisplayProps) => {
  const [activeTab, setActiveTab] = useState("resumo");
  
  // Dados básicos
  const valorInvestimento = parametrosFinanceiros.valorTotal || 0;
  const geracaoMensalEstimada = kitSolar.geracaoEstimada || 0;
  const tarifaMediaEnergia = 0.85; // R$/kWh - valor médio nacional
  const inflacaoMediaEnergia = 0.10; // 10% ao ano - valor aproximado
  const vidaUtilSistema = 30; // anos (garantia dos painéis)
  const degradacaoAnual = 0.007; // 0.7% ao ano - degradação média dos painéis
  
  // Cálculos financeiros
  const economiaMensal = geracaoMensalEstimada * tarifaMediaEnergia;
  const economiaAnual = economiaMensal * 12;
  const taxaJurosMercado = 0.085; // 8.5% ao ano - rendimento médio
  const inflacaoMedio = 0.045; // 4.5% ao ano 
  
  // Financiamento
  const taxaFinanciamento = 0.12; // 12% ao ano
  const prazoFinanciamento = 8; // 8 anos (96 meses)
  const parcelaMensal = valorInvestimento * (taxaFinanciamento / 12) * Math.pow(1 + taxaFinanciamento / 12, prazoFinanciamento * 12) / (Math.pow(1 + taxaFinanciamento / 12, prazoFinanciamento * 12) - 1);
  
  // Comparação de investimentos
  const rendimentoMercado = (valorInvestimento * taxaJurosMercado) / 12; // mensal
  
  // Payback
  const paybackMeses = valorInvestimento / economiaMensal;
  const paybackAnos = paybackMeses / 12;
  
  // ROI
  const roiAnual = (economiaAnual / valorInvestimento) * 100;
  
  // Dados para o gráfico de fluxo de caixa
  const gerarDadosFluxoCaixa = () => {
    const dados = [];
    let economiaAcumulada = 0;
    let investimento = -valorInvestimento;
    
    // Primeiro registro - momento da compra
    dados.push({
      ano: 0,
      economiaMensal: 0,
      economiaAcumulada: 0,
      fluxoCaixa: investimento,
      valorPresente: investimento
    });
    
    for (let ano = 1; ano <= 30; ano++) {
      // Considerar degradação e inflação da energia
      const fatorDegradacao = Math.pow(1 - degradacaoAnual, ano);
      const fatorInflacaoEnergia = Math.pow(1 + inflacaoMediaEnergia, ano);
      const fatorInflacao = Math.pow(1 + inflacaoMedio, ano);
      
      // Economia ajustada pela degradação e inflação da energia
      const economiaAnualAjustada = economiaAnual * fatorDegradacao * fatorInflacaoEnergia;
      economiaAcumulada += economiaAnualAjustada;
      
      // Fluxo de caixa
      const fluxoCaixa = economiaAcumulada + investimento;
      
      // Valor presente
      const valorPresente = economiaAnualAjustada / Math.pow(1 + taxaJurosMercado, ano) + investimento;
      
      dados.push({
        ano,
        economiaAnual: economiaAnualAjustada,
        economiaAcumulada,
        fluxoCaixa,
        valorPresente
      });
    }
    
    return dados;
  };
  
  const dadosFluxoCaixa = gerarDadosFluxoCaixa();
  
  // Dados para o gráfico de comparação
  const dadosComparativo = [
    { nome: "Energia Convencional", valor: dadosInstalacao.consumoMensal * tarifaMediaEnergia * 12 * 30 },
    { nome: "Sistema Solar", valor: valorInvestimento + 0.1 * valorInvestimento } // Investimento + manutenção estimada
  ];
  
  // Dados para o gráfico de financiamento
  const dadosFinanciamento = Array.from({ length: prazoFinanciamento * 12 }, (_, i) => {
    const mes = i + 1;
    const fatorInflacaoEnergia = Math.pow(1 + inflacaoMediaEnergia / 12, mes);
    const economiaMensalAjustada = economiaMensal * fatorInflacaoEnergia;
    
    return {
      mes,
      parcelaMensal,
      economiaMensal: economiaMensalAjustada,
      diferenca: economiaMensalAjustada - parcelaMensal
    };
  });

  return (
    <Card className="mt-6 shadow-md animate-fade-in">
      <CardHeader className="bg-green-50/50">
        <CardTitle className="flex items-center gap-2 text-green-700">
          <Calculator className="h-6 w-6 text-green-600" />
          Análise Financeira do Sistema Solar
        </CardTitle>
        <CardDescription>
          Resultados financeiros e comparativos de investimento para sua solução fotovoltaica
        </CardDescription>
      </CardHeader>
      
      <CardContent className="p-4 md:p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6 p-1 bg-muted/20 rounded-lg w-full">
            <TabsTrigger 
              value="resumo" 
              className="flex items-center gap-2 py-2 data-[state=active]:bg-green-50 data-[state=active]:text-green-700 data-[state=active]:shadow-sm"
            >
              <CircleDollarSign className="h-4 w-4" />
              <span className="hidden sm:inline">Resumo</span>
            </TabsTrigger>
            <TabsTrigger 
              value="fluxocaixa" 
              className="flex items-center gap-2 py-2 data-[state=active]:bg-green-50 data-[state=active]:text-green-700 data-[state=active]:shadow-sm"
            >
              <ChartBar className="h-4 w-4" />
              <span className="hidden sm:inline">Fluxo de Caixa</span>
            </TabsTrigger>
            <TabsTrigger 
              value="financiamento" 
              className="flex items-center gap-2 py-2 data-[state=active]:bg-green-50 data-[state=active]:text-green-700 data-[state=active]:shadow-sm"
            >
              <Calendar className="h-4 w-4" />
              <span className="hidden sm:inline">Financiamento</span>
            </TabsTrigger>
            <TabsTrigger 
              value="comparativo" 
              className="flex items-center gap-2 py-2 data-[state=active]:bg-green-50 data-[state=active]:text-green-700 data-[state=active]:shadow-sm"
            >
              <ChartPie className="h-4 w-4" />
              <span className="hidden sm:inline">Comparativo</span>
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="resumo" className="animate-fade-in focus-visible:outline-none focus-visible:ring-0">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-green-50 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <p className="text-sm text-gray-600 mb-1">Investimento Total</p>
                <p className="text-2xl font-bold text-green-700">
                  {valorInvestimento.toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  })}
                </p>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <p className="text-sm text-gray-600 mb-1">Economia Mensal</p>
                <p className="text-2xl font-bold text-green-700">
                  {economiaMensal.toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  })}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Geração: {geracaoMensalEstimada} kWh/mês × {tarifaMediaEnergia.toFixed(2)} R$/kWh
                </p>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <p className="text-sm text-gray-600 mb-1">Payback Simples</p>
                <p className="text-2xl font-bold text-green-700">
                  {paybackAnos.toFixed(1)} anos
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {Math.round(paybackMeses)} meses
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-lg font-semibold mb-2 text-green-700 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Retorno do Investimento
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>ROI Anual:</span>
                    <span className="font-semibold">{roiAnual.toFixed(2)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>ROI em 30 anos:</span>
                    <span className="font-semibold">{(roiAnual * 30).toFixed(2)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Comparado a investimento de mercado:</span>
                    <span className="font-semibold">{(roiAnual / taxaJurosMercado * 100).toFixed(2)}% do rendimento</span>
                  </div>
                </div>
              </div>
              
              <div className="border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-lg font-semibold mb-2 text-green-700 flex items-center gap-2">
                  <ClipboardCheck className="h-4 w-4" />
                  Economia a Longo Prazo
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Economia em 10 anos:</span>
                    <span className="font-semibold">
                      {(economiaMensal * 12 * 10).toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Economia em 25 anos:</span>
                    <span className="font-semibold">
                      {(economiaMensal * 12 * 25).toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Economia em toda vida útil (30 anos):</span>
                    <span className="font-semibold">
                      {(economiaMensal * 12 * 30).toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
              <h3 className="text-lg font-semibold mb-2 text-green-700 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Financiamento
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm mb-1">Parcela Mensal (estimada)</p>
                  <p className="text-xl font-semibold">
                    {parcelaMensal.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-sm mb-1">Economia Mensal</p>
                  <p className="text-xl font-semibold">
                    {economiaMensal.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-sm mb-1">Diferença</p>
                  <p className={`text-xl font-semibold ${economiaMensal > parcelaMensal ? 'text-green-600' : 'text-red-600'}`}>
                    {(economiaMensal - parcelaMensal).toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Simulação baseada em taxa de {(taxaFinanciamento * 100).toFixed(1)}% ao ano em {prazoFinanciamento} anos de financiamento
              </p>
            </div>
          </TabsContent>
          
          <TabsContent value="fluxocaixa" className="animate-fade-in focus-visible:outline-none focus-visible:ring-0">
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2 text-green-700 flex items-center gap-2">
                <ChartBar className="h-5 w-5" />
                Fluxo de Caixa ao Longo da Vida Útil
              </h3>
              <p className="text-sm text-gray-600">
                O gráfico mostra a economia acumulada e o fluxo de caixa ao longo dos 30 anos de vida útil do sistema.
              </p>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
              <div className="h-[220px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={dadosFluxoCaixa}
                    margin={{ top: 5, right: 5, left: 5, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="ano" 
                      tick={{ fontSize: 10 }}
                      label={{ 
                        value: 'Anos', 
                        position: 'insideBottomRight', 
                        offset: -10,
                        fontSize: 10 
                      }}
                    />
                    <YAxis 
                      tickFormatter={(value) => 
                        new Intl.NumberFormat('pt-BR', {
                          notation: 'compact',
                          compactDisplay: 'short',
                          style: 'currency',
                          currency: 'BRL'
                        }).format(value)
                      } 
                      tick={{ fontSize: 10 }}
                      width={40}
                    />
                    <Tooltip 
                      formatter={(value) => 
                        new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL'
                        }).format(Number(value))
                      }
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '0.375rem',
                        fontSize: '11px',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Legend 
                      verticalAlign="top" 
                      height={30}
                      iconSize={8}
                      iconType="circle"
                      wrapperStyle={{ fontSize: '10px' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="fluxoCaixa" 
                      name="Fluxo de Caixa" 
                      stroke="#10b981" 
                      activeDot={{ r: 4 }} 
                      strokeWidth={1.5}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="economiaAcumulada" 
                      name="Economia Acumulada" 
                      stroke="#0ea5e9" 
                      strokeWidth={1.5}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-lg font-semibold mb-2 text-green-700">Detalhes a Longo Prazo</h3>
                <div className="space-y-2">
                  {[5, 10, 15, 20, 25, 30].map((anos) => {
                    const dadoAno = dadosFluxoCaixa.find(d => d.ano === anos);
                    if (!dadoAno) return null;
                    
                    return (
                      <div key={anos} className="flex justify-between">
                        <span>Após {anos} anos:</span>
                        <span className={`font-semibold ${dadoAno.fluxoCaixa > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {dadoAno.fluxoCaixa.toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-lg font-semibold mb-2 text-green-700">Análise Financeira</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm mb-1">Tempo para ROI Positivo</p>
                    <p className="text-xl font-semibold text-green-600">
                      {Math.ceil(paybackAnos)} anos
                    </p>
                  </div>
                  <div>
                    <p className="text-sm mb-1">Economia Total em 30 anos</p>
                    <p className="text-xl font-semibold text-green-600">
                      {dadosFluxoCaixa[30].economiaAcumulada.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm mb-1">Fluxo de Caixa em 30 anos</p>
                    <p className="text-xl font-semibold text-green-600">
                      {dadosFluxoCaixa[30].fluxoCaixa.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="financiamento" className="animate-fade-in focus-visible:outline-none focus-visible:ring-0">
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2 text-green-700 flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Comparativo: Parcela vs. Economia Mensal
              </h3>
              <p className="text-sm text-gray-600">
                Comparação entre o valor da parcela do financiamento e a economia mensal gerada pelo sistema solar.
              </p>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
              <div className="h-[220px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={dadosFinanciamento}
                    margin={{ top: 5, right: 5, left: 5, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="mes" 
                      tick={{ fontSize: 10 }}
                      label={{ 
                        value: 'Meses', 
                        position: 'insideBottomRight', 
                        offset: -10,
                        fontSize: 10 
                      }}
                    />
                    <YAxis 
                      tickFormatter={(value) => 
                        new Intl.NumberFormat('pt-BR', {
                          notation: 'compact',
                          compactDisplay: 'short',
                          style: 'currency',
                          currency: 'BRL'
                        }).format(value)
                      } 
                      tick={{ fontSize: 10 }}
                      width={40}
                    />
                    <Tooltip 
                      formatter={(value) => 
                        new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL'
                        }).format(Number(value))
                      }
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '0.375rem',
                        fontSize: '11px',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Legend 
                      verticalAlign="top" 
                      height={25}
                      iconSize={8}
                      iconType="circle"
                      wrapperStyle={{ fontSize: '10px' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="parcelaMensal" 
                      name="Parcela Mensal" 
                      stroke="#f43f5e" 
                      strokeWidth={1.5}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="economiaMensal" 
                      name="Economia Mensal" 
                      stroke="#10b981" 
                      strokeWidth={1.5}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="diferenca" 
                      name="Diferença" 
                      stroke="#6366f1" 
                      strokeWidth={1.5}
                      strokeDasharray="4 4"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-lg font-semibold mb-2 text-red-600 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Financiamento
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Valor financiado:</span>
                    <span className="font-semibold">
                      {valorInvestimento.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Parcela mensal:</span>
                    <span className="font-semibold">
                      {parcelaMensal.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Prazo:</span>
                    <span className="font-semibold">{prazoFinanciamento} anos ({prazoFinanciamento * 12} meses)</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Taxa:</span>
                    <span className="font-semibold">{(taxaFinanciamento * 100).toFixed(1)}% ao ano</span>
                  </div>
                </div>
              </div>
              
              <div className="border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-lg font-semibold mb-2 text-green-600 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Economia
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Economia inicial:</span>
                    <span className="font-semibold">
                      {economiaMensal.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Economia final:</span>
                    <span className="font-semibold">
                      {dadosFinanciamento[dadosFinanciamento.length-1].economiaMensal.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Aumento:</span>
                    <span className="font-semibold">
                      {((dadosFinanciamento[dadosFinanciamento.length-1].economiaMensal / economiaMensal - 1) * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Fator de inflação energética:</span>
                    <span className="font-semibold">{(inflacaoMediaEnergia * 100).toFixed(1)}% ao ano</span>
                  </div>
                </div>
              </div>
              
              <div className="border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-lg font-semibold mb-2 text-indigo-600 flex items-center gap-2">
                  <ChartBar className="h-4 w-4" />
                  Balanço
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Diferença inicial:</span>
                    <span className={`font-semibold ${dadosFinanciamento[0].diferenca >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {dadosFinanciamento[0].diferenca.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Diferença final:</span>
                    <span className={`font-semibold ${dadosFinanciamento[dadosFinanciamento.length-1].diferenca >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {dadosFinanciamento[dadosFinanciamento.length-1].diferenca.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Meses com fluxo positivo:</span>
                    <span className="font-semibold">
                      {dadosFinanciamento.filter(d => d.diferenca >= 0).length} de {dadosFinanciamento.length}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Economia líquida durante financiamento:</span>
                    <span className="font-semibold">
                      {dadosFinanciamento.reduce((total, d) => total + d.diferenca, 0).toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="comparativo" className="animate-fade-in focus-visible:outline-none focus-visible:ring-0">
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2 text-green-700 flex items-center gap-2">
                <ChartPie className="h-5 w-5" />
                Comparativo de Custos: 30 Anos
              </h3>
              <p className="text-sm text-gray-600">
                Comparação entre o custo total da energia convencional e o sistema solar ao longo de 30 anos.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-4 rounded-lg shadow-sm h-[220px] flex items-center">
                <ResponsiveContainer width="100%" height="90%">
                  <BarChart
                    data={dadosComparativo}
                    margin={{ top: 5, right: 5, left: 5, bottom: 20 }}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      type="number"
                      tick={{ fontSize: 10 }}
                      tickFormatter={(value) => 
                        new Intl.NumberFormat('pt-BR', {
                          notation: 'compact',
                          compactDisplay: 'short',
                          style: 'currency',
                          currency: 'BRL'
                        }).format(value)
                      }
                    />
                    <YAxis 
                      dataKey="nome" 
                      type="category"
                      tick={{ fontSize: 10 }}
                      width={90}
                    />
                    <Tooltip 
                      formatter={(value) => 
                        new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL'
                        }).format(Number(value))
                      }
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '0.375rem',
                        fontSize: '11px',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Legend 
                      verticalAlign="top"
                      height={25}
                      iconSize={8}
                      iconType="circle"
                      wrapperStyle={{ fontSize: '10px' }}
                    />
                    <Bar dataKey="valor" name="Custo Total (30 anos)" radius={[0, 4, 4, 0]}>
                      <Cell fill="#ef4444" />
                      <Cell fill="#10b981" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              
              <div className="border rounded-lg p-6 flex flex-col justify-center shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-xl font-semibold mb-4 text-green-700">Economia Total em 30 Anos</h3>
                <div className="text-center">
                  <p className="text-3xl font-bold text-green-600 mb-2">
                    {(dadosComparativo[0].valor - dadosComparativo[1].valor).toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </p>
                  <p className="text-gray-600">
                    Economia de {((dadosComparativo[0].valor / dadosComparativo[1].valor - 1) * 100).toFixed(1)}% em relação à energia convencional
                  </p>
                </div>
                
                <div className="mt-8 space-y-4">
                  <div className="flex justify-between items-center p-2 rounded-md hover:bg-gray-50">
                    <span className="flex items-center">
                      <span className="w-4 h-4 inline-block bg-red-500 mr-2 rounded-md"></span>
                      Custo com Energia Convencional:
                    </span>
                    <span className="font-semibold text-red-600">
                      {dadosComparativo[0].valor.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center p-2 rounded-md hover:bg-gray-50">
                    <span className="flex items-center">
                      <span className="w-4 h-4 inline-block bg-green-500 mr-2 rounded-md"></span>
                      Custo com Sistema Solar:
                    </span>
                    <span className="font-semibold text-green-600">
                      {dadosComparativo[1].valor.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
